import { auth, db, storage, functions } from '../config/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc, query, where, addDoc, orderBy } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

export const adminService = {
  // Get all users from Firestore
  getAllUsers: async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const users = [];
      
      usersSnap.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      
      return { users, error: null };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { users: [], error: error.message };
    }
  },

  // Update user in Firestore
  updateUser: async (userId, updates) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  },

  // Grant access to user
  grantAccess: async (userId) => {
    return await adminService.updateUser(userId, { suspended: false });
  },

  // Suspend user access
  suspendUser: async (userId) => {
    return await adminService.updateUser(userId, { suspended: true });
  },

  // Set user as admin
  setAsAdmin: async (userId) => {
    return await adminService.updateUser(userId, { userType: 'admin' });
  },

  // Set user as regular user
  setAsUser: async (userId) => {
    return await adminService.updateUser(userId, { userType: 'user' });
  },

  // Delete user account (deletes from Firestore)
  deleteUser: async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  },

  // Reset user password (sends password reset email)
  resetUserPassword: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      let errorMessage = 'Failed to send password reset email';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No user found with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // Create user manually (admin only) - Uses Firebase Cloud Function via HTTP
  createUser: async (email, firstName, lastName, userType = 'user', password = null) => {
    try {
      // Get the current user's ID token for authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { success: false, error: 'You must be logged in to create users' };
      }
      
      const idToken = await currentUser.getIdToken();
      
      // Determine the function URL (emulator or production)
      const isEmulator = typeof window !== 'undefined' && 
        localStorage.getItem('firebase_emulator_connected') === 'true';
      
      const functionUrl = isEmulator
        ? 'http://127.0.0.1:5012/twelve-month-millionaire/us-central1/createUserAdmin'
        : 'https://us-central1-twelve-month-millionaire.cloudfunctions.net/createUserAdmin';
      
      // Call the cloud function via HTTP
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          userType,
          password, // Optional - if null, temp password generated
        }),
      });

      const data = await response.json();

      if (data.success) {
        let message = 'User created successfully!';
        if (data.requiresPasswordReset && data.tempPassword) {
          message += `\n\nTemporary password: ${data.tempPassword}\n\nPlease send this to the user or have them reset their password.`;
        }
        return { 
          success: true, 
          userId: data.userId,
          tempPassword: data.tempPassword,
          requiresPasswordReset: data.requiresPasswordReset,
          message,
          error: null 
        };
      } else {
        return { success: false, error: data.error || 'Failed to create user' };
      }
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Handle specific error codes
      let errorMessage = 'Failed to create user';
      if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // Update maintenance settings
  updateMaintenanceSettings: async (settings) => {
    console.log('[AdminService] updateMaintenanceSettings called with:', settings);
    try {
      const maintenanceRef = doc(db, 'site_maintenance', 'global');
      console.log('[AdminService] Writing to Firestore...');
      await setDoc(maintenanceRef, {
        ...settings,
        updatedAt: new Date(),
      }, { merge: true });
      console.log('[AdminService] Write successful');
      return { success: true, error: null };
    } catch (error) {
      console.error('[AdminService] Error updating maintenance settings:', error);
      return { success: false, error: error.message };
    }
  },

  // ==================== COURSE MANAGEMENT ====================

  // Get all courses
  getAllCourses: async () => {
    try {
      const coursesRef = collection(db, 'courses');
      const coursesQuery = query(coursesRef, orderBy('order', 'asc'));
      const coursesSnap = await getDocs(coursesQuery);
      const courses = [];
      
      coursesSnap.forEach((doc) => {
        courses.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      
      return { courses, error: null };
    } catch (error) {
      console.error('Error fetching courses:', error);
      return { courses: [], error: error.message };
    }
  },

  // Create new course
  createCourse: async (courseData) => {
    try {
      const coursesRef = collection(db, 'courses');
      const docRef = await addDoc(coursesRef, {
        ...courseData,
        chapters: courseData.chapters || [],
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return { success: true, courseId: docRef.id, error: null };
    } catch (error) {
      console.error('Error creating course:', error);
      return { success: false, error: error.message };
    }
  },

  // Update course
  updateCourse: async (courseId, updates) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      await updateDoc(courseRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating course:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete course
  deleteCourse: async (courseId) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      await deleteDoc(courseRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting course:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload image to Firebase Storage
  uploadCourseImage: async (file, courseId, fileName) => {
    try {
      const storageRef = ref(storage, `courses/${courseId}/${fileName}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return { success: true, url: downloadURL, error: null };
    } catch (error) {
      console.error('Error uploading image:', error);
      return { success: false, url: null, error: error.message };
    }
  },

  // Upload course video
  uploadCourseVideo: async (file, courseId, chapterId, fileName, onProgress) => {
    try {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        return { success: false, url: null, error: 'File must be a video' };
      }

      // Create storage reference
      const storageRef = ref(storage, `courses/${courseId}/videos/${chapterId}/${fileName}`);
      
      // Create upload task with progress monitoring
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Return a promise that resolves when upload completes
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Progress monitoring
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload is ${progress.toFixed(1)}% done`);
            
            // Call progress callback if provided
            if (onProgress) {
              onProgress(progress, snapshot.bytesTransferred, snapshot.totalBytes);
            }
          },
          (error) => {
            // Error handling
            console.error('Upload error:', error);
            let errorMessage = 'Upload failed';
            
            switch (error.code) {
              case 'storage/unauthorized':
                errorMessage = 'You do not have permission to upload files';
                break;
              case 'storage/canceled':
                errorMessage = 'Upload was canceled';
                break;
              case 'storage/quota-exceeded':
                errorMessage = 'Storage quota exceeded';
                break;
              case 'storage/retry-limit-exceeded':
                errorMessage = 'Upload timeout - file may be too large or connection too slow';
                break;
              default:
                errorMessage = error.message || 'Unknown error occurred';
            }
            
            resolve({ success: false, url: null, error: errorMessage });
          },
          async () => {
            // Upload completed successfully, get download URL
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('Video uploaded successfully:', downloadURL);
              resolve({ success: true, url: downloadURL, error: null });
            } catch (error) {
              console.error('Error getting download URL:', error);
              resolve({ success: false, url: null, error: 'Upload succeeded but could not get URL' });
            }
          }
        );
      });
    } catch (error) {
      console.error('Error starting video upload:', error);
      return { success: false, url: null, error: error.message };
    }
  },

  // ========== Widget Management ==========

  // Get all widgets
  getAllWidgets: async () => {
    try {
      const widgetsRef = collection(db, 'widgets');
      const widgetsQuery = query(widgetsRef, orderBy('createdAt', 'desc'));
      const widgetsSnap = await getDocs(widgetsQuery);
      const widgets = [];

      widgetsSnap.forEach((doc) => {
        widgets.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { widgets, error: null };
    } catch (error) {
      console.error('Error fetching widgets:', error);
      return { widgets: [], error: error.message };
    }
  },

  // Create new widget
  createWidget: async (widgetData) => {
    try {
      const widgetsRef = collection(db, 'widgets');
      const newWidget = {
        ...widgetData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const docRef = await addDoc(widgetsRef, newWidget);
      return { success: true, id: docRef.id, error: null };
    } catch (error) {
      console.error('Error creating widget:', error);
      return { success: false, id: null, error: error.message };
    }
  },

  // Update widget
  updateWidget: async (widgetId, updates) => {
    try {
      const widgetRef = doc(db, 'widgets', widgetId);
      await updateDoc(widgetRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating widget:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete widget
  deleteWidget: async (widgetId) => {
    try {
      const widgetRef = doc(db, 'widgets', widgetId);
      await deleteDoc(widgetRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting widget:', error);
      return { success: false, error: error.message };
    }
  },
};

